import { Col, InputNumber, Row, Select, Typography } from "antd";
import { IVendorPurchaseCode } from "common/interface/IVendor";
import { IWarehousePurchaseCode } from "common/interface/ISkuPurchaseCode";

const { Text } = Typography;

interface PurchaseCodesFieldProps {
  /** Injected by antd Form.Item */
  value?: IVendorPurchaseCode[];
  onChange?: (value: IVendorPurchaseCode[]) => void;
  options: IWarehousePurchaseCode[];
  loading?: boolean;
}

/**
 * Maps purchase SKU codes to a vendor with a reliability score per code,
 * mirroring the WMS Flutter app's "Map Purchase SKU" picker.
 * Form value shape matches the API contract: [{ purchaseSkuCode, relibilityScore }]
 */
export const PurchaseCodesField = ({ value, onChange, options, loading }: PurchaseCodesFieldProps) => {
  const codes = Array.isArray(value) ? value : [];

  const skuName = (code: string) =>
    options.find((o) => o.skuCode === code)?.name;

  const onCodesChange = (selected: string[]) => {
    onChange?.(
      selected.map(
        (code) =>
          codes.find((c) => c.purchaseSkuCode === code) ?? {
            purchaseSkuCode: code,
            relibilityScore: 0,
          }
      )
    );
  };

  const onScoreChange = (code: string, score: number | null) => {
    onChange?.(
      codes.map((c) =>
        c.purchaseSkuCode === code ? { ...c, relibilityScore: score ?? 0 } : c
      )
    );
  };

  return (
    <>
      <Select
        mode="multiple"
        placeholder="Select purchase SKUs for this vendor"
        allowClear
        showSearch
        loading={loading}
        optionFilterProp="label"
        value={codes.map((c) => c.purchaseSkuCode)}
        onChange={onCodesChange}
        options={options.map((pc) => ({
          value: pc.skuCode,
          label: `${pc.name} (${pc.skuCode})`,
        }))}
      />
      {codes.length > 0 && (
        <div style={{ marginTop: 12, border: "1px solid #f0f0f0", borderRadius: 8, padding: "8px 12px" }}>
          <Row gutter={8} style={{ marginBottom: 4 }}>
            <Col span={16}>
              <Text type="secondary" style={{ fontSize: 12 }}>Purchase SKU</Text>
            </Col>
            <Col span={8}>
              <Text type="secondary" style={{ fontSize: 12 }}>Reliability Score</Text>
            </Col>
          </Row>
          {codes.map((c) => (
            <Row gutter={8} key={c.purchaseSkuCode} align="middle" style={{ marginBottom: 6 }}>
              <Col span={16}>
                <Text style={{ fontSize: 13 }}>
                  {skuName(c.purchaseSkuCode)
                    ? `${skuName(c.purchaseSkuCode)} (${c.purchaseSkuCode})`
                    : c.purchaseSkuCode}
                </Text>
              </Col>
              <Col span={8}>
                <InputNumber
                  min={0}
                  max={99}
                  precision={0}
                  value={c.relibilityScore}
                  onChange={(score) => onScoreChange(c.purchaseSkuCode, score)}
                  style={{ width: "100%" }}
                />
              </Col>
            </Row>
          ))}
        </div>
      )}
    </>
  );
};
